import { useStorage } from '@vueuse/core'
import { Ref, watch } from 'vue'
import { URL_TOKEN_PARAM, STORAGE_TOKEN_KEY } from '@/enum/generic'

/**
 * Token + Tab Isolated Storage Utility
 *
 * This utility provides BOTH token AND tab isolation for localStorage.
 *
 * Storage key format: key_token_<tokenHash>_tab_<tabId>
 *
 * How it works:
 * - Token comes from URL (?token=xxx) - isolates different users
 * - Tab ID comes from sessionStorage - isolates tabs of same user
 * - F5 refresh = same token + same tab ID = state preserved ✓
 * - New tab (same token) = same token + NEW tab ID = fresh state ✓
 * - Different token = different namespace entirely ✓
 *
 * Features:
 * - Token isolation: Different users have separate data
 * - Tab isolation: Same user can have different state per tab
 * - Refresh persistence: F5 preserves state (sessionStorage survives refresh)
 * - Auto-cleanup: Keeps only MAX_TOKENS most recent tokens
 */

// Separators used in localStorage keys: "key_token_abc123_tab_xyz789"
const TOKEN_SEPARATOR = '_token_'
const TAB_SEPARATOR = '_tab_'
// Suffix for "last state" per token (used to bootstrap new tabs)
const LAST_STATE_SUFFIX = '_lastState'
// SessionStorage key for the unique tab ID
const TAB_ID_SESSION_KEY = '__tabId__'
// Key for tracking active tokens and their last access time
const TOKEN_REGISTRY_KEY = '__tokenRegistry__'
// Maximum number of tokens to keep in storage
const MAX_TOKENS = 4

/**
 * Gets the token from URL query parameters
 */
function getTokenFromUrl (): string | null {
    const urlParams = new URLSearchParams(window.location.search)
    return urlParams.get(URL_TOKEN_PARAM)
}

/**
 * Creates a hash of a string using djb2 algorithm
 * Fast, simple, good distribution - perfect for localStorage keys
 */
function hashString (str: string): string {
    let hash = 5381
    for (let i = 0; i < str.length; i++) {
        hash = ((hash << 5) + hash) ^ str.charCodeAt(i)
    }
    return Math.abs(hash).toString(36)
}

/**
 * Gets a short hash of the token for use as storage key
 */
function getShortToken (token: string): string {
    if (!token) return 'anonymous'
    return hashString(token)
}

/**
 * Gets or creates a unique tab ID using sessionStorage
 *
 * sessionStorage advantages:
 * - Persists across F5 refresh (survives page reload)
 * - UNIQUE per browser tab (never inherited when opening new tabs)
 * - Unlike window.name, sessionStorage is NOT copied on Ctrl+Click or window.open()
 */
function ensureTabId (): string {
    let tabId = sessionStorage.getItem(TAB_ID_SESSION_KEY)

    if (!tabId) {
        // Generate new unique tab ID
        const timestamp = Date.now()
        const random = Math.random().toString(36).substring(2, 9)
        tabId = `${timestamp}_${random}`

        sessionStorage.setItem(TAB_ID_SESSION_KEY, tabId)
        console.log('[Tab Storage] Created new tab ID:', tabId)
    }

    return tabId
}

/**
 * Gets the current token identifier
 * Priority: URL token → stored token → 'anonymous'
 */
export function getCurrentTokenId (): string {
    const urlToken = getTokenFromUrl()
    if (urlToken) {
        return getShortToken(urlToken)
    }

    const storedToken = localStorage.getItem(STORAGE_TOKEN_KEY)
    if (storedToken) {
        return getShortToken(storedToken)
    }

    return 'anonymous'
}

/**
 * Gets the full storage namespace combining token + tab
 * Format: token_<hash>_tab_<id>
 */
function getStorageNamespace (): string {
    const tokenId = getCurrentTokenId()
    const tabId = ensureTabId()
    return `${tokenId}${TAB_SEPARATOR}${tabId}`
}

/**
 * Token registry entry - tracks when each token was last used
 */
interface TokenRegistryEntry {
    tokenId: string
    lastAccess: number
}

/**
 * Gets the token registry from localStorage
 */
function getTokenRegistry (): TokenRegistryEntry[] {
    try {
        const data = localStorage.getItem(TOKEN_REGISTRY_KEY)
        return data ? JSON.parse(data) : []
    } catch {
        return []
    }
}

/**
 * Saves the token registry to localStorage
 */
function saveTokenRegistry (registry: TokenRegistryEntry[]): void {
    localStorage.setItem(TOKEN_REGISTRY_KEY, JSON.stringify(registry))
}

/**
 * Clears data for a specific token (all tabs of that token)
 */
function clearTokenData (tokenId: string): void {
    const tokenPattern = `${TOKEN_SEPARATOR}${tokenId}${TAB_SEPARATOR}`
    const keysToRemove: string[] = []

    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key && key.includes(tokenPattern)) {
            keysToRemove.push(key)
        }
    }

    keysToRemove.forEach(key => {
        localStorage.removeItem(key)
    })
}

/**
 * Registers a token access and cleans up old tokens if needed
 */
function registerTokenAccess (tokenId: string): void {
    if (tokenId === 'anonymous') return

    const registry = getTokenRegistry()
    const now = Date.now()

    const existingIndex = registry.findIndex(e => e.tokenId === tokenId)
    if (existingIndex >= 0) {
        registry[existingIndex].lastAccess = now
    } else {
        registry.push({
            tokenId,
            lastAccess: now
        })
    }

    registry.sort((a, b) => b.lastAccess - a.lastAccess)

    if (registry.length > MAX_TOKENS) {
        const tokensToRemove = registry.slice(MAX_TOKENS)

        tokensToRemove.forEach(entry => {
            clearTokenData(entry.tokenId)
            console.log(`[Tab Storage] Cleaned up old token: ${entry.tokenId}`)
        })

        const newRegistry = registry.slice(0, MAX_TOKENS)
        saveTokenRegistry(newRegistry)
    } else {
        saveTokenRegistry(registry)
    }
}

/**
 * Gets the lastState key for a given base key and token
 * Used to bootstrap new tabs with the last state from previous tabs
 */
function getLastStateKey (key: string, tokenId: string): string {
    return `${key}${TOKEN_SEPARATOR}${tokenId}${LAST_STATE_SUFFIX}`
}

/**
 * Wrapper around useStorage with token + tab isolation
 *
 * Features:
 * - Token isolation: Different tokens = different users = separate data
 * - Tab isolation: Same token, different tabs = separate data
 * - F5 refresh: Same tab ID (sessionStorage persists) = state preserved
 * - New tab: Inherits last state from previous tab with same token
 *
 * @param key - The base storage key
 * @param defaultValue - Default value if key doesn't exist
 * @param options - Optional useStorage options
 * @returns Ref with the stored value
 *
 * @example
 * const accountId = useTabStorage('selectedAccount', undefined)
 * // Actual localStorage key: 'selectedAccount_token_abc123_tab_xyz789'
 */
export function useTabStorage<T> (
    key: string,
    defaultValue: T,
    options?: any
): Ref<T> {
    const tokenId = getCurrentTokenId()
    const namespace = getStorageNamespace()
    const namespacedKey = `${key}${TOKEN_SEPARATOR}${namespace}`
    const lastStateKey = getLastStateKey(key, tokenId)

    // Register token access for cleanup
    registerTokenAccess(tokenId)

    // Check if this tab already has data
    const existingData = localStorage.getItem(namespacedKey)

    let initialValue = defaultValue

    if (existingData) {
        // This tab has data - use it
        console.log(`[Tab Storage] Loaded '${key}' for tab ${namespace}`)
    } else {
        // New tab - try to inherit from lastState (previous tab with same token)
        const lastStateData = localStorage.getItem(lastStateKey)
        if (lastStateData) {
            try {
                initialValue = JSON.parse(lastStateData) as T
                // Pre-populate this tab's storage with inherited state
                localStorage.setItem(namespacedKey, lastStateData)
                console.log(`[Tab Storage] Inherited '${key}' from previous tab (token: ${tokenId})`)
            } catch (e) {
                console.warn(`[Tab Storage] Failed to parse lastState for '${key}'`, e)
            }
        } else {
            console.log(`[Tab Storage] No data for '${key}', using default`)
        }
    }

    // Create the storage ref
    const storage = useStorage(namespacedKey, initialValue, localStorage, {
        ...options,
        listenToStorageChanges: false
    })

    // Watch for changes and update lastState (for future tabs to inherit)
    watch(storage, (newValue) => {
        try {
            localStorage.setItem(lastStateKey, JSON.stringify(newValue))
        } catch (e) {
            console.warn(`[Tab Storage] Failed to save lastState for '${key}'`, e)
        }
    }, {
        deep: true,
        immediate: true
    })

    return storage
}

/**
 * Clears ALL token/tab specific data from localStorage
 *
 * WARNING: This will clear data for ALL tokens and tabs!
 *
 * Use cases:
 * - Version upgrades (data structure may have changed)
 * - Global cache invalidation
 */
export function clearAllTabSpecificData (): void {
    const keysToRemove: string[] = []

    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key && (key.includes(TOKEN_SEPARATOR) || key.includes(LAST_STATE_SUFFIX) || key === TOKEN_REGISTRY_KEY)) {
            keysToRemove.push(key)
        }
    }

    keysToRemove.forEach(key => {
        localStorage.removeItem(key)
    })

    // Reset tab ID
    sessionStorage.removeItem(TAB_ID_SESSION_KEY)

    console.log(`[Tab Storage] Cleared ${keysToRemove.length} keys from localStorage`)
}
